import { Field, Form, Formik } from 'formik';
import { useRouter } from '@uirouter/react';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { Button } from '@@/buttons';

import { GitCredentialFormValues, Provider, AuthTypeOption } from '../types';

import { validationSchema } from './GitCredentialForm.validation';
import { GitCredentialSection } from './GitCredentialSection';

type Props = {
  isEditing?: boolean;
  isLoading: boolean;
  onSubmit: (formValues: GitCredentialFormValues) => void;
  gitCredentialNames: string[];
  initialValues?: GitCredentialFormValues;
};

const defaultInitialValues = {
  name: '',
  username: '',
  password: '',
  provider: Provider.Custom,
  authorizationType: AuthTypeOption.Basic,
};

export function GitCredentialForm({
  isEditing = false,
  isLoading,
  onSubmit,
  gitCredentialNames,
  initialValues = defaultInitialValues,
}: Props) {
  const router = useRouter();

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={() => validationSchema(gitCredentialNames, isEditing)}
      onSubmit={(values) => onSubmit(values)}
      validateOnMount
    >
      {({ values, errors, handleSubmit, isValid, dirty, setFieldValue }) => (
        <Form className="form-horizontal" onSubmit={handleSubmit} noValidate>
          <FormControl
            inputId="Name"
            label="Name"
            errors={errors.name}
            required
          >
            <Field
              as={Input}
              name="name"
              value={values.name}
              autoComplete="off"
              id="Name"
            />
          </FormControl>

          <GitCredentialSection
            provider={values.provider}
            authorizationType={values.authorizationType}
            username={values.username ?? ''}
            password={values.password}
            isEditing={isEditing}
            errors={errors}
            onChange={({ provider, username, password, authorizationType }) => {
              if (provider !== undefined) setFieldValue('provider', provider);
              if (username !== undefined) setFieldValue('username', username);
              if (password !== undefined) setFieldValue('password', password);
              if (authorizationType !== undefined)
                setFieldValue('authorizationType', authorizationType);
            }}
          />

          <div className="form-group">
            <div className="col-sm-12 mt-3">
              <LoadingButton
                disabled={!isValid || !dirty}
                data-cy="git-credential-save-button"
                isLoading={isLoading}
                loadingText="Saving Git Credential..."
              >
                {isEditing ? 'Update git credential' : 'Save git credential'}
              </LoadingButton>
              {isEditing && (
                <Button
                  color="default"
                  data-cy="git-credential-cancel-button"
                  onClick={() => router.stateService.go('portainer.account')}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
